import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import settingsService from '../services/settingsService'

const ENV = {
  adminEnabled: process.env.REACT_APP_ADMIN_ADSENSE_ENABLED,
  adminClient: process.env.REACT_APP_ADMIN_ADSENSE_CLIENT,
  adminSlot: process.env.REACT_APP_ADMIN_ADSENSE_SLOT,
  adminTestMode: process.env.REACT_APP_ADMIN_ADSENSE_TEST_MODE,
  homeEnabled: process.env.REACT_APP_HOME_ADSENSE_ENABLED,
  homeClient: process.env.REACT_APP_HOME_ADSENSE_CLIENT,
  homeSlot: process.env.REACT_APP_HOME_ADSENSE_SLOT,
  homeTestMode: process.env.REACT_APP_HOME_ADSENSE_TEST_MODE,
}

const toBoolean = (value, defaultValue = false) => {
  if (value === undefined || value === null || value === '') return defaultValue
  if (typeof value === 'boolean') return value
  const normalized = String(value).toLowerCase()
  return normalized === 'true' || normalized === '1' || normalized === 'yes'
}

const normalizeClientId = (value) => {
  const raw = (value || '').toString().trim()
  if (!raw) return ''
  if (raw.startsWith('ca-pub-')) return raw
  const digitsOnly = raw.replace(/[^0-9]/g, '')
  return digitsOnly ? `ca-pub-${digitsOnly}` : raw
}

const normalizeSlotId = (value) => {
  return (value || '').toString().trim()
}

const ensureAdSenseScript = (client) => {
  if (!client) return Promise.resolve(false)

  const src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`

  if (document.querySelector(`script[src="${src}"]`)) {
    return Promise.resolve(true)
  }

  return new Promise((resolve) => {
    const script = document.createElement('script')
    script.async = true
    script.src = src
    script.crossOrigin = 'anonymous'
    script.onload = () => resolve(true)
    script.onerror = () => resolve(false)
    document.head.appendChild(script)
  })
}

const AdminAdSenseBanner = () => {
  const location = useLocation()
  const { user, isAuthenticated } = useAuth()
  const adRef = useRef(null)
  const [serverSettings, setServerSettings] = useState(null)

  useEffect(() => {
    let mounted = true

    const loadSettings = async () => {
      try {
        const [publicResponse, adminResponse] = await Promise.allSettled([
          settingsService.getPublicSettings('general'),
          isAuthenticated && user?.role === 'admin'
            ? settingsService.getAllSettings('general')
            : Promise.resolve(null),
        ])

        const publicSettings =
          publicResponse.status === 'fulfilled' && publicResponse.value?.success
            ? publicResponse.value.settings || {}
            : {}

        const adminSettingsArray =
          adminResponse.status === 'fulfilled' && adminResponse.value?.success
            ? adminResponse.value.settings || []
            : []

        const adminSettings = Array.isArray(adminSettingsArray)
          ? adminSettingsArray.reduce((accumulator, item) => {
              accumulator[item.setting_key] = item.setting_value
              return accumulator
            }, {})
          : {}

        if (mounted) {
          setServerSettings({
            ...publicSettings,
            ...adminSettings,
          })
        }
      } catch (error) {
        if (mounted) {
          setServerSettings({})
        }
      }
    }

    loadSettings()

    return () => {
      mounted = false
    }
  }, [isAuthenticated, user?.role, location.pathname])

  const adminAdConfig = useMemo(() => {
    const fromServer = serverSettings || {}

    return {
      enabled: toBoolean(fromServer.admin_adsense_enabled, toBoolean(ENV.adminEnabled, false)),
      client: normalizeClientId(fromServer.admin_adsense_client || ENV.adminClient || ''),
      slot: normalizeSlotId(fromServer.admin_adsense_slot || ENV.adminSlot || ''),
      testMode: toBoolean(fromServer.admin_adsense_test_mode, toBoolean(ENV.adminTestMode, false)),
    }
  }, [serverSettings])

  const homeAdConfig = useMemo(() => {
    const fromServer = serverSettings || {}

    return {
      enabled: toBoolean(ENV.homeEnabled, toBoolean(fromServer.home_adsense_enabled, false)),
      client: normalizeClientId(ENV.homeClient || fromServer.home_adsense_client || ''),
      slot: normalizeSlotId(ENV.homeSlot || fromServer.home_adsense_slot || ''),
      testMode: toBoolean(ENV.homeTestMode, toBoolean(fromServer.home_adsense_test_mode, false)),
    }
  }, [serverSettings])

  const isAdminRoute = location.pathname.startsWith('/admin')
  const isHomeRoute = location.pathname === '/'

  const activeConfig = useMemo(() => {
    if (isAdminRoute && isAuthenticated && user?.role === 'admin') {
      return adminAdConfig
    }

    if (isHomeRoute) {
      return homeAdConfig
    }

    return null
  }, [isAdminRoute, isAuthenticated, user?.role, adminAdConfig, isHomeRoute, homeAdConfig])

  const shouldRender = !!(
    activeConfig &&
    activeConfig.enabled &&
    activeConfig.client &&
    activeConfig.slot
  )

  useEffect(() => {
    if (!shouldRender || !adRef.current) return

    let isDisposed = false

    const initAd = async () => {
      const loaded = await ensureAdSenseScript(activeConfig.client)
      if (!loaded || isDisposed || !adRef.current) return

      if (adRef.current.dataset.adsbygoogleStatus === 'done') return

      try {
        ;(window.adsbygoogle = window.adsbygoogle || []).push({})
      } catch (error) {}
    }

    initAd()

    return () => {
      isDisposed = true
    }
  }, [shouldRender, activeConfig?.client, activeConfig?.slot])

  if (!shouldRender) return null

  return (
    <div className="w-full h-[50px] max-h-[50px] bg-gray-100 border-t border-gray-200 overflow-hidden">
      <div className="max-w-7xl mx-auto h-[50px] max-h-[50px] px-4 sm:px-6 lg:px-8">
        <div className="h-[50px] max-h-[50px] overflow-hidden bg-white">
          <ins
            key={`${activeConfig.client}-${activeConfig.slot}-${activeConfig.testMode ? 'test' : 'live'}-${isHomeRoute ? 'home' : 'admin'}`}
            ref={adRef}
            className="adsbygoogle"
            style={{ display: 'block', width: '100%', height: '50px' }}
            data-ad-client={activeConfig.client}
            data-ad-slot={activeConfig.slot}
            data-ad-format="horizontal"
            data-full-width-responsive="false"
            data-adtest={activeConfig.testMode ? 'on' : 'off'}
          />
        </div>
      </div>
    </div>
  )
}

export default AdminAdSenseBanner
